import { Blob, File } from '@consigli/types';

/**
 * Downloads a file from a blob's download URL
 */
export const downloadBlob = async (blob: Blob): Promise<void> => {
  try {
    if (!blob.downloadUrl) {
      throw new Error('Download URL is not available');
    }
    const response = await fetch(blob.downloadUrl);
    if (!response.ok) {
      throw new Error('Network response was not ok');
    }
    const fileBlob = await response.blob();

    // Create download link
    const url = window.URL.createObjectURL(fileBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = blob.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error: unknown) {
    console.error('Error downloading file:', error);
    throw error;
  }
};

/**
 * Downloads a file from a file's URL
 */
export const downloadFile = async (file: File): Promise<void> => {
  try {
    if (!file.fileUrl) {
      throw new Error('File URL is not available');
    }
    const response = await fetch(file.fileUrl);
    if (!response.ok) {
      throw new Error('Network response was not ok');
    }
    const fileBlob = await response.blob();

    // Create download link
    const url = window.URL.createObjectURL(fileBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = file.filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error: unknown) {
    console.error('Error downloading file:', error);
    throw error;
  }
};

/**
 * Downloads either a Blob or File object
 */
export const downloadItem = async (item: Blob | File): Promise<void> => {
  if ('name' in item) {
    await downloadBlob(item);
  } else if ('filename' in item) {
    await downloadFile(item);
  } else {
    throw new Error('Unknown item type for download');
  }
};

/**
 * Extracts the base name (without extension) from a filename
 */
export const getBaseName = (filename: string): string => {
  const lastDotIndex = filename.lastIndexOf('.');
  return lastDotIndex === -1 ? filename : filename.substring(0, lastDotIndex);
};

/**
 * Extracts the extension from a filename
 */
export const getExtension = (filename: string): string => {
  const lastDotIndex = filename.lastIndexOf('.');
  return lastDotIndex === -1 ? '' : filename.substring(lastDotIndex);
};

/**
 * Creates a new filename by combining a base name with an extension
 */
export const createFilename = (baseName: string, extension: string): string => {
  return baseName.trim() + extension;
};
